import { type FormEvent, useState } from 'react';
import {
  ERROR_CODES,
  type PersonDto,
  createPersonBodySchema,
  suggestColorIndex,
  updatePersonBodySchema,
} from '@betreuung/shared';
import { ApiError, NetworkError } from '@/api/http';
import { useCreatePerson, useUpdatePerson } from '@/api/mutations';
import { ResponsiveModal } from '@/components/shared/ResponsiveModal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { RadioCard, RadioGroup } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { useIsDesktop } from '@/app/useMediaQuery';
import { cn } from '@/lib/utils';
import { de } from '@/i18n/de';
import { ColorPickerGrid } from './ColorPickerGrid';

export interface PersonSheetProps {
  /** `null` opens the sheet in create mode. */
  person: PersonDto | null;
  /** Every person — the colour grid marks the colours already in use. */
  persons: PersonDto[];
  onClose: () => void;
}

const FIELD_LABEL = 'block pt-3.5 pb-[5px] text-label font-semibold text-fg-secondary';

const ACCESS_ACCOUNT = 'account';
const ACCESS_NONE = 'none';
const ACCESS_VIEWER = 'viewer';

/**
 * Access is one axis with three values, not two independent switches: that is
 * what makes "view-only without a login" — a row nothing could reach —
 * unrepresentable in the form.
 *
 * The third option spans the full row rather than squeezing three cards into
 * one, following the assignment modal's own treatment of "Nicht zugewiesen".
 */
const ACCESS_OPTIONS = [
  { value: ACCESS_NONE, label: de.personSheet.withoutAccount, wide: false },
  { value: ACCESS_ACCOUNT, label: de.personSheet.withAccount, wide: false },
  { value: ACCESS_VIEWER, label: de.personSheet.viewOnly, wide: true },
] as const;

/**
 * Create and edit a person — one form for both.
 *
 * The payload is validated against the shared request schemas before it is sent,
 * so the rules the server enforces (a name, a password for a new account, admin
 * only with an account) are reported here in the same words instead of coming
 * back as a generic failure.
 */
export function PersonSheet({ person, persons, onClose }: PersonSheetProps) {
  const isDesktop = useIsDesktop();
  const createPerson = useCreatePerson();
  const updatePerson = useUpdatePerson();

  const [name, setName] = useState(person?.name ?? '');
  const [hasAccount, setHasAccount] = useState(person?.hasAccount ?? false);
  const [isCaregiver, setIsCaregiver] = useState(person?.isCaregiver ?? true);
  const [isAdmin, setIsAdmin] = useState(person?.isAdmin ?? false);
  const [password, setPassword] = useState('');
  const [colorIndex, setColorIndex] = useState(
    () =>
      person?.colorIndex ??
      suggestColorIndex(
        persons.filter((entry) => entry.isCaregiver).map((entry) => entry.colorIndex),
      ),
  );
  const [error, setError] = useState<string | null>(null);

  const pending = createPerson.isPending || updatePerson.isPending;
  const access = !hasAccount ? ACCESS_NONE : isCaregiver ? ACCESS_ACCOUNT : ACCESS_VIEWER;

  const submit = async () => {
    if (pending) return;
    setError(null);

    const fields = {
      name: name.trim(),
      colorIndex,
      hasAccount,
      // A person without an account is always a Betreuungsperson: view-only is a
      // property of an account, and the radio group cannot express anything else.
      isCaregiver: hasAccount ? isCaregiver : true,
      isAdmin: hasAccount && isCaregiver && isAdmin,
      // An empty field means "keep the current password": `undefined` is dropped
      // by JSON.stringify, so the key never reaches the server.
      password: password === '' ? undefined : password,
    };

    const parsed = person
      ? updatePersonBodySchema.safeParse(fields)
      : createPersonBodySchema.safeParse(fields);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? de.errors.generic);
      return;
    }

    try {
      if (person) await updatePerson.mutateAsync({ id: person.id, ...fields });
      else await createPerson.mutateAsync(fields);
      onClose();
    } catch (caught) {
      setError(personErrorMessage(caught));
    }
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void submit();
  };

  return (
    <ResponsiveModal
      open
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      title={person ? de.personSheet.editTitle : de.personSheet.createTitle}
    >
      <form onSubmit={onSubmit} noValidate>
        <label htmlFor="person-name" className={FIELD_LABEL}>
          {de.personSheet.name}
        </label>
        <Input
          id="person-name"
          autoComplete="off"
          autoCapitalize="words"
          placeholder={de.personSheet.namePlaceholder}
          value={name}
          onChange={(event) => setName(event.target.value)}
        />

        <p className={FIELD_LABEL}>{de.personSheet.access}</p>
        <RadioGroup
          value={access}
          onValueChange={(next) => {
            setHasAccount(next !== ACCESS_NONE);
            setIsCaregiver(next !== ACCESS_VIEWER);
            // Administration is a permission on an account, and a view-only
            // account is precisely one that changes nothing — so neither state
            // can carry it.
            if (next !== ACCESS_ACCOUNT) setIsAdmin(false);
          }}
          aria-label={de.personSheet.access}
          className="grid grid-cols-2 gap-2"
        >
          {ACCESS_OPTIONS.map((option) => {
            const selected = option.value === access;
            return (
              <RadioCard
                key={option.value}
                value={option.value}
                className={cn(
                  'flex min-h-11 items-center justify-center rounded-control',
                  option.wide && 'col-span-2',
                  // The selected card's thicker border is compensated by one
                  // pixel less padding, keeping the card size stable.
                  selected ? 'border-2 border-accent p-2.5' : 'border border-line p-[11px]',
                )}
              >
                <span
                  className={
                    selected
                      ? 'text-control font-semibold text-fg'
                      : 'text-control font-medium text-fg-muted'
                  }
                >
                  {option.label}
                </span>
              </RadioCard>
            );
          })}
        </RadioGroup>

        {access === ACCESS_VIEWER ? (
          <p className="pt-1.5 text-duration text-fg-hint">{de.personSheet.viewOnlyHint}</p>
        ) : null}

        {hasAccount ? (
          <>
            <label htmlFor="person-password" className={FIELD_LABEL}>
              {de.personSheet.password}
            </label>
            <Input
              id="person-password"
              type="password"
              autoComplete="new-password"
              placeholder={de.personSheet.passwordPlaceholder}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
            {person?.hasAccount ? (
              <p className="pt-1.5 text-duration text-fg-hint">{de.personSheet.passwordKeep}</p>
            ) : null}
          </>
        ) : null}

        <div className="flex items-center justify-between gap-3 pt-3.5">
          <div className="min-w-0">
            <label htmlFor="person-admin" className="text-label font-semibold text-fg-secondary">
              {de.personSheet.adminPermission}
            </label>
            <p className="pt-0.5 text-duration text-fg-hint">
              {de.personSheet.adminPermissionHint}
            </p>
          </div>
          <Switch
            id="person-admin"
            checked={hasAccount && isCaregiver && isAdmin}
            disabled={!hasAccount || !isCaregiver}
            onCheckedChange={setIsAdmin}
          />
        </div>

        <p className="block pt-4 pb-2 text-label font-semibold text-fg-secondary">
          {de.personSheet.color}
        </p>
        <ColorPickerGrid
          value={colorIndex}
          onChange={setColorIndex}
          persons={persons}
          excludePersonId={person?.id ?? null}
        />

        {error ? (
          <p role="alert" className="pt-3 text-label text-cal-9">
            {error}
          </p>
        ) : null}

        {isDesktop ? (
          <div className="flex items-center justify-end gap-2 pt-[18px]">
            <Button
              variant="outline"
              size="dialog"
              type="button"
              onClick={onClose}
              disabled={pending}
            >
              {de.personSheet.cancel}
            </Button>
            <Button size="dialog" type="submit" disabled={pending || name.trim() === ''}>
              {pending ? de.modal.saving : de.personSheet.save}
            </Button>
          </div>
        ) : (
          <div className="grid gap-2 pt-4">
            <Button type="submit" disabled={pending || name.trim() === ''}>
              {pending ? de.modal.saving : de.personSheet.save}
            </Button>
            <Button variant="text" size="text" type="button" onClick={onClose} disabled={pending}>
              {de.personSheet.cancel}
            </Button>
          </div>
        )}
      </form>
    </ResponsiveModal>
  );
}

/**
 * The four person failures the user can act on. Exported because deleting a
 * person happens outside this sheet but fails the same way.
 */
export function personErrorMessage(error: unknown): string {
  if (error instanceof NetworkError) return de.errors.offline;
  if (!(error instanceof ApiError)) return de.errors.generic;

  switch (error.code) {
    case ERROR_CODES.NAME_TAKEN:
      return de.errors.nameTaken;
    case ERROR_CODES.FORBIDDEN:
      return de.errors.lastAdmin;
    case ERROR_CODES.PERSON_HAS_ASSIGNMENTS:
      return de.errors.personHasAssignments;
    default:
      return de.errors.generic;
  }
}
